import { gql } from '@apollo/client'
import { BaseOfferBlock, CollectionGridBlockFragment } from 'app/features/offer/blocks'
import { LinkOnlyRedemptionBlockFragment, MultipleCodesRedemptionBlockFragment, Redemption, SingleCodeRedemptionBlockFragment } from 'app/features/offer/redemptions'
import {
  AccordianBlockFragment,
  FaqsBlockFragment,
  FeaturedContentCarouselBlockFragment,
  HeadingBlockFragment,
  ImageBlockFragment,
  JumpToLinksBlockFragment,
  RichTextBlockFragment,
  SpacerBlockFragment,
} from 'app/features/shared/blocks'
import { VideoBlockFragment } from 'app/features/shared/blocks/video-block'
import { usePersistedCacheQuery } from 'app/hooks'

export const fetchOfferQuery = gql`
  query fetchOffer($slug: String!, $draft: Boolean!) {
    Offers(where: { slug: { equals: $slug } }, draft: $draft) {
      docs {
        id
        name
        description
        tag
        brand {
          id
          name
          slug
          logo {
            id
            url
            alt
          }
        }
        thumbnailImage {
          id
          url
        }
        featuredImage {
          id
          url
        }
        slug
        userType
        canRedeem
        embedUrl
        content {
          ${AccordianBlockFragment.partialFragment}
          ${CollectionGridBlockFragment.partialFragment}
          ${FaqsBlockFragment.partialFragment}
          ${FeaturedContentCarouselBlockFragment.partialFragment}
          ${HeadingBlockFragment.partialFragment}
          ${ImageBlockFragment.partialFragment}
          ${JumpToLinksBlockFragment.partialFragment}
          ${RichTextBlockFragment.partialFragment}
          ${SpacerBlockFragment.partialFragment}
          ${VideoBlockFragment.partialFragment}
        }
        offerLinkLabel
        meta {
          title
          description
        }
        redemptions {
          ${LinkOnlyRedemptionBlockFragment.partialFragment}
          ${MultipleCodesRedemptionBlockFragment.partialFragment}
          ${SingleCodeRedemptionBlockFragment.partialFragment}
        }
        isBookmarked
        updatedAt
      }
    }
    fetchRedemption(slug: $slug) {
      message
      offerLink
      redeemCode
      redemptionInstructions
      validUntil
    }
  }
`

export interface OfferQueryInput {
  slug: string
  draft: boolean
}

export interface OfferQueryOutput {
  Offers: {
    docs: {
      id: string
      name: string
      description: string
      tag?: string
      brand: {
        id: string
        name: string
        slug: string
        logo: {
          url: string
        }
      }
      thumbnailImage: {
        url: string
      }
      featuredImage: {
        url: string
      }
      slug: string
      userType: string[]
      canRedeem: boolean
      embedUrl: string
      content: BaseOfferBlock[]
      offerLinkLabel?: string
      meta: {
        title?: string
        description?: string
      }
      redemptions: Redemption[]
      isBookmarked: boolean
      updatedAt: string
    }[]
  }
  fetchRedemption: {
    message: string
    offerLink: string
    redeemCode: string
    redemptionInstructions: string
    validUntil?: string
  }
}

export type Offer = OfferQueryOutput['Offers']['docs'][0]

export const useOfferQuery = (input: OfferQueryInput) =>
  usePersistedCacheQuery<OfferQueryOutput, OfferQueryInput>(fetchOfferQuery, { variables: input })
